import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { setLastAuthMethod } from '~/common/auth/last-auth-method'
import type { OAuthProvider } from '~/common/auth/providers'
import { getAuthRoute, getOAuthUrl } from '~/common/utils/auth-routes'

export interface WorkOSUserInfo {
  id: string
  email: string
  firstName: string
  lastName: string
}

interface SignInResponse {
  user: WorkOSUserInfo
}

interface ErrorResponse {
  message?: string
}

interface OAuthUrlResponse {
  authorizationUrl: string
}

export interface WorkOSAuthContextValue {
  user: WorkOSUserInfo | null
  isLoading: boolean
  isSigningIn: boolean
  isSignedIn: boolean
  signIn: (email: string, password: string) => Promise<WorkOSUserInfo>
  signInWithCode: (email: string, code: string) => Promise<WorkOSUserInfo>
  signInWithOAuth: (provider: OAuthProvider, redirectPath?: string) => Promise<void>
  signOut: () => void
  error: Error | null
}

const WorkOSAuthContext = createContext<WorkOSAuthContextValue | null>(null)

/**
 * Provider for WorkOS authentication state.
 * ARP uses WorkOS exclusively (no Firebase support).
 *
 * Manages auth state in a single location and runs check-session once on mount.
 * All components using useWorkOSAuthContext will share this state.
 */
export const WorkOSAuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<WorkOSUserInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * Checks if there's a valid session cookie and restores user state.
   * Called once on provider mount to restore authentication across page refreshes.
   */
  const checkSession = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(getAuthRoute('check-session'), {
        method: 'POST',
        credentials: 'include', // Send httpOnly sealed session cookie
      })

      if (!response.ok) {
        // No valid session (401) or other error - user is not authenticated
        setUser(null)
        setIsLoading(false)
        return
      }

      const data = (await response.json()) as SignInResponse
      setUser(data.user)
    } catch {
      // Network error or other issue - treat as not authenticated
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Sync with external session state on mount (legitimate external system sync)
  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect -- syncing with external session state
    void checkSession()
  }, [checkSession])

  /**
   * Signs in a user with email and password via WorkOS.
   * Backend returns user info + sets sealed session cookie.
   */
  const signIn = useCallback(async (email: string, password: string): Promise<WorkOSUserInfo> => {
    setIsSigningIn(true)
    setError(null)

    try {
      const response = await fetch(getAuthRoute('sign-in'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Receives httpOnly sealed session cookie
        body: JSON.stringify({ email, password }),
      })

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as ErrorResponse
        throw new Error(errorData.message ?? 'Failed to sign in')
      }

      const data = (await response.json()) as SignInResponse
      setUser(data.user)
      setLastAuthMethod('email')

      // Notify other tabs/windows to sync their state (timestamp for uniqueness)
      const timestamp = Temporal.Now.instant().epochMilliseconds
      localStorage.setItem(
        'workos_auth_event',
        JSON.stringify({ type: 'signin', user: data.user, timestamp }),
      )

      return data.user
    } catch (err) {
      const authError = err instanceof Error ? err : new Error('Sign in failed')
      setError(authError)
      throw authError
    } finally {
      setIsSigningIn(false)
    }
  }, [])

  /**
   * Signs in with a code emailed to the user. Mirrors `signIn`: the session arrives as an
   * httpOnly cookie and only the user record comes back.
   */
  const signInWithCode = useCallback(
    async (email: string, code: string): Promise<WorkOSUserInfo> => {
      setIsSigningIn(true)
      setError(null)

      try {
        const response = await fetch(getAuthRoute('magic-auth/verify-code'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ email, code }),
        })

        if (!response.ok) {
          const errorData = (await response.json().catch(() => ({}))) as ErrorResponse
          throw new Error(errorData.message ?? 'Failed to verify code')
        }

        const data = (await response.json()) as SignInResponse
        setUser(data.user)
        setLastAuthMethod('code')

        const timestamp = Temporal.Now.instant().epochMilliseconds
        localStorage.setItem(
          'workos_auth_event',
          JSON.stringify({ type: 'signin', user: data.user, timestamp }),
        )

        return data.user
      } catch (err) {
        const authError = err instanceof Error ? err : new Error('Failed to verify code')
        setError(authError)
        throw authError
      } finally {
        setIsSigningIn(false)
      }
    },
    [],
  )

  /**
   * Signs out the user by revoking the session and clearing the cookie.
   */
  const signOut = useCallback(() => {
    // Clear state immediately
    setUser(null)

    // Notify other tabs to sign out (timestamp for uniqueness)
    const timestamp = Temporal.Now.instant().epochMilliseconds
    localStorage.setItem('workos_auth_event', JSON.stringify({ type: 'signout', timestamp }))

    // Call backend to clear httpOnly cookie (fire and forget)
    fetch(getAuthRoute('sign-out'), {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {
      // Ignore errors - we've already cleared local state
    })
  }, [])

  // Listen for auth changes from other tabs (cross-tab sync via storage event)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== 'workos_auth_event' || !event.newValue) {
        return
      }

      try {
        const authEvent = JSON.parse(event.newValue) as {
          type: string
          user?: WorkOSUserInfo
        }

        if (authEvent.type === 'signout') {
          setUser(null)
        } else if (authEvent.type === 'signin' && authEvent.user) {
          setUser(authEvent.user)
        }
      } catch {
        // Ignore parse errors
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  /**
   * Initiates Google or Microsoft OAuth sign-in through WorkOS.
   */
  const signInWithOAuth = useCallback(
    async (provider: OAuthProvider, redirectPath?: string): Promise<void> => {
      setIsSigningIn(true)
      setError(null)

      try {
        const url = new URL(getOAuthUrl(provider))
        if (redirectPath) {
          url.searchParams.set('state', redirectPath)
        }

        const response = await fetch(url.toString(), {
          method: 'GET',
          credentials: 'include',
        })

        if (!response.ok) {
          const errorData = (await response.json().catch(() => ({}))) as ErrorResponse
          throw new Error(errorData.message ?? 'Failed to start OAuth sign in')
        }

        const data = (await response.json()) as OAuthUrlResponse
        setLastAuthMethod(provider)
        window.location.href = data.authorizationUrl
      } catch (err) {
        const authError = err instanceof Error ? err : new Error('Sign in failed')
        setError(authError)
        setIsSigningIn(false)
        throw authError
      }
    },
    [],
  )

  const value: WorkOSAuthContextValue = {
    user,
    isLoading,
    isSigningIn,
    isSignedIn: !!user,
    signIn,
    signInWithCode,
    signOut,
    signInWithOAuth,
    error,
  }

  return <WorkOSAuthContext.Provider value={value}>{children}</WorkOSAuthContext.Provider>
}

/**
 * Hook for accessing WorkOS authentication state.
 *
 * Security Model:
 * - ALL tokens stored in httpOnly sealed session cookie (server-managed, encrypted)
 * - Frontend stores ZERO tokens (immune to XSS attacks)
 * - Browser automatically sends cookie with every request (credentials: 'include')
 *
 * @returns Auth state and methods (user, isLoading, isSignedIn, signIn, signOut, error)
 * @throws Error if used outside of WorkOSAuthProvider
 */
export const useWorkOSAuthContext = (): WorkOSAuthContextValue => {
  const context = useContext(WorkOSAuthContext)

  if (!context) {
    throw new Error('useWorkOSAuthContext must be used within WorkOSAuthProvider')
  }

  return context
}
